(() => {
  if (customElements.get('product-size-chart')) return;

  class ProductSizeChart extends HTMLElement {
    static unit(value) {
      const unit = String(value).trim().toLowerCase();
      if (/^(cm|centimet(?:er|re)s?)$/.test(unit)) return 'cm';
      if (/^(in|inch(?:es)?)$/.test(unit)) return 'in';
      return null;
    }

    static number(value) {
      const text = value.trim().replace(',', '.');
      let number;
      if (/^\d+(?:\.\d+)?$/.test(text)) number = Number(text);
      else {
        const fraction = text.match(/^(?:(\d+)\s+)?(\d+)\/(\d+)$/);
        if (!fraction || Number(fraction[3]) === 0) return null;
        number = Number(fraction[1] || 0) + Number(fraction[2]) / Number(fraction[3]);
      }
      return number > 0 && number <= 10000 ? number : null;
    }

    static measurement(value, unit) {
      let text = value.trim();
      if (/^(?:|[-–—]|n\/?a)$/i.test(text)) return { text: text || '—' };
      const suffix = text.match(/\s*(cm|centimet(?:er|re)s?|in|inch(?:es)?)$/i);
      if (suffix) {
        if (this.unit(suffix[1]) !== unit) return null;
        text = text.slice(0, suffix.index).trim();
      }
      const values = text.split(/\s*(?:[-–—]|\bto\b)\s*/i);
      if (values.length > 2) return null;
      const numbers = values.map((part) => this.number(part));
      if (numbers.some((number) => number === null) || (numbers.length === 2 && numbers[0] > numbers[1])) return null;
      return { numbers, unit };
    }

    static parse(source) {
      if (typeof source !== 'string' || source.length > 65535) return null;
      // Preserve trailing tabs: an empty spreadsheet cell is still a column.
      const lines = source.replace(/^\uFEFF/, '').split(/\r\n?|\n/).map((line) => line.replace(/^ +| +$/g, '')).filter((line) => line.trim());
      if (lines.length < 2 || lines.length > 102) return null;
      let defaultUnit = null;
      const declaration = lines[0].match(/^units?\s*:\s*(.+)$/i);
      if (declaration) {
        defaultUnit = this.unit(declaration[1]);
        if (!defaultUnit) return null;
        lines.shift();
      }
      const delimiter = lines[0].includes('\t') ? '\t' : '|';
      const split = (line) => {
        if (delimiter === '|') line = line.replace(/^\|/, '').replace(/\|$/, '');
        return line.split(delimiter).map((cell) => {
          const text = cell.trim();
          return text.startsWith('"') && text.endsWith('"') ? text.slice(1, -1).replace(/""/g, '"') : text;
        });
      };
      const headers = split(lines.shift());
      if (headers.length < 2 || headers.length > 10 || headers.some((header) => !header || header.length > 100)) return null;
      const columns = headers.map((header, index) => {
        // A numeric size or an EU/US label is never a physical measurement.
        if (index === 0) return { label: header, unit: null };
        const match = header.match(/\s*(?:\((cm|in|inches|inch|centimet(?:er|re)s?)\)|\[(cm|in|inches|inch|centimet(?:er|re)s?)\]|\s+(cm|in|inches|inch|centimet(?:er|re)s?))$/i);
        const unit = match ? this.unit(match[1] || match[2] || match[3]) : /^(?:us|uk|eu|euro|size|fit|note)$/i.test(header) ? null : defaultUnit;
        return { label: match ? header.slice(0, match.index).trim() : header, unit };
      });
      if (!columns.some((column) => column.unit) || columns.some((column) => !column.label)) return null;
      const rows = [];
      const sizes = new Set();
      for (const line of lines) {
        const cells = split(line);
        if (cells.every((cell) => /^:?-{3,}:?$/.test(cell))) continue;
        if (cells.length !== columns.length || cells.some((cell) => cell.length > 100) || !cells[0]) return null;
        const key = this.normalizeSize(cells[0]);
        if (sizes.has(key)) return null;
        sizes.add(key);
        const values = cells.map((cell, index) => columns[index].unit ? this.measurement(cell, columns[index].unit) : { text: cell || '—' });
        if (values.some((value) => value === null)) return null;
        rows.push({ size: cells[0], values });
      }
      if (!rows.length || rows.length > 100 || !rows.some((row) => row.values.some((cell) => cell.numbers))) return null;
      return { columns, rows };
    }

    static normalizeSize(value) {
      return String(value ?? '').trim().replace(/^size\s+/i, '').replace(/\s+/g, ' ').toUpperCase();
    }

    static format(cell, unit) {
      if (!cell.numbers) return cell.text;
      return cell.numbers.map((number) => {
        const converted = cell.unit === unit ? number : cell.unit === 'cm' ? number / 2.54 : number * 2.54;
        return converted.toLocaleString('en-US', { maximumFractionDigits: 1 });
      }).join('–');
    }

    constructor() {
      super();
      this.onClose = this.finishClose.bind(this);
      this.onCancel = (event) => {
        event.preventDefault();
        event.stopPropagation();
        document.addEventListener('keyup', (release) => {
          if (release.key === 'Escape') release.stopPropagation();
        }, { capture: true, once: true });
        this.close();
      };
      this.onKeydown = this.handleKeydown.bind(this);
      this.addEventListener('click', (event) => {
        const unit = event.target.closest('[data-size-chart-unit]');
        const row = event.target.closest('[data-size-chart-row]');
        if (unit) this.setUnit(unit.dataset.sizeChartUnit);
        else if (event.target.closest('[data-size-chart-close]')) this.close();
        else if (event.target.closest('[data-size-chart-apply]')) this.applySize();
        else if (row) this.selectRow(row);
        else if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
      });
    }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      try {
        this.model = ProductSizeChart.parse(this.querySelector('[data-size-chart-source]').content.textContent);
      } catch {
        this.model = null;
      }
      for (const opener of this.openers) opener.hidden = !this.model;
      const error = this.querySelector('[data-size-chart-error]');
      if (error) error.hidden = Boolean(this.model);
      if (!this.model) return;
      this.buildTable();
      this.setUnit('in');
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (!this.dialog) return;
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('keydown', this.onKeydown);
      this.restoreFocus = false;
      if (this.dialog.open) this.dialog.close();
      this.finishClose();
    }

    get openers() {
      return [...document.querySelectorAll('[data-product-size-chart-open]')].filter((button) => button.dataset.productSizeChartOpen === this.dialog?.id);
    }

    get optionGroup() {
      return document.getElementById(this.dataset.pickerId)?.querySelector('[data-product-size-chart-option]');
    }

    get rows() {
      return [...this.querySelectorAll('[data-size-chart-row]')];
    }

    buildTable() {
      const head = document.createElement('tr');
      for (const column of this.model.columns) {
        const cell = document.createElement('th');
        cell.scope = 'col';
        head.append(cell);
      }
      this.querySelector('thead').replaceChildren(head);
      const body = document.createDocumentFragment();
      for (const [index, data] of this.model.rows.entries()) {
        const row = document.createElement('tr');
        row.dataset.sizeChartRow = String(index);
        row.dataset.size = data.size;
        const heading = document.createElement('th');
        heading.scope = 'row';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'shoe-size-guide__row-button';
        button.setAttribute('aria-pressed', 'false');
        button.textContent = data.size;
        heading.append(button);
        row.append(heading);
        for (let col = 1; col < this.model.columns.length; col++) row.append(document.createElement('td'));
        body.append(row);
      }
      this.querySelector('tbody').replaceChildren(body);
    }

    setUnit(unit) {
      if (!this.model || !['in', 'cm'].includes(unit)) return;
      this.unit = unit;
      this.querySelectorAll('[data-size-chart-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.sizeChartUnit === unit)));
      this.querySelector('[data-size-chart-status]').textContent = `Measurements in ${unit === 'in' ? 'inches' : 'centimeters'}`;
      this.querySelectorAll('thead th').forEach((cell, index) => {
        const column = this.model.columns[index];
        cell.textContent = column.label + (column.unit ? ` (${unit})` : '');
      });
      this.rows.forEach((row, index) => {
        row.querySelectorAll('td').forEach((cell, col) => {
          cell.textContent = ProductSizeChart.format(this.model.rows[index].values[col + 1], unit);
        });
      });
    }

    choiceFor(row) {
      const size = ProductSizeChart.normalizeSize(row.dataset.size);
      const choices = this.optionGroup?.querySelectorAll('select option, input[type="radio"]') || [];
      const choice = [...choices].find((input) => ProductSizeChart.normalizeSize(input.value) === size);
      if (!choice) return null;
      return {
        choice,
        control: choice.tagName === 'OPTION' ? choice.closest('select') : choice,
        available: !choice.disabled && !choice.classList.contains('disabled') && choice.dataset.optionAvailable !== 'false',
      };
    }

    show(opener) {
      if (!this.model || !this.dialog || this.dialog.open || typeof this.dialog.showModal !== 'function') return;
      this.opener = opener;
      this.focusTarget = null;
      this.restoreFocus = true;
      const group = this.optionGroup;
      const current = group?.querySelector('select')?.value || group?.querySelector('input:checked')?.value;
      this.rows.forEach((row) => row.classList.toggle('is-unavailable', Boolean(group && !this.choiceFor(row)?.available)));
      this.selectRow(this.rows.find((row) => ProductSizeChart.normalizeSize(row.dataset.size) === ProductSizeChart.normalizeSize(current)));
      this.dialog.showModal();
      document.body.classList.add('product-size-chart-open');
      opener.setAttribute('aria-expanded', 'true');
      this.selectedRow?.scrollIntoView({ block: 'nearest' });
    }

    selectRow(row) {
      this.selectedRow = row;
      this.rows.forEach((item) => {
        item.classList.toggle('is-selected', item === row);
        item.querySelector('button').setAttribute('aria-pressed', String(item === row));
      });
      this.querySelector('[data-size-chart-selected]').textContent = row?.dataset.size || '—';
      const apply = this.querySelector('[data-size-chart-apply]');
      apply.hidden = !this.optionGroup;
      apply.disabled = !row || !this.choiceFor(row)?.available;
      this.querySelector('[data-size-chart-apply-label]').textContent = row && apply.disabled ? this.dataset.unavailableLabel : this.dataset.applyLabel;
    }

    applySize() {
      if (!this.selectedRow) return;
      const match = this.choiceFor(this.selectedRow);
      if (!match?.available) {
        this.selectRow(this.selectedRow);
        return;
      }
      const { choice, control } = match;
      const changed = choice.tagName === 'OPTION' ? control.value !== choice.value : !choice.checked;
      if (choice.tagName === 'OPTION') control.value = choice.value;
      else choice.checked = true;
      this.focusTarget = control;
      this.close();
      if (changed) control.dispatchEvent(new Event('change', { bubbles: true }));
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
    }

    finishClose() {
      if (!document.querySelector('product-size-chart dialog[open]')) document.body.classList.remove('product-size-chart-open');
      const opener = this.openers[0];
      opener?.setAttribute('aria-expanded', 'false');
      if (!this.isConnected || !this.restoreFocus) return;
      const target = (this.focusTarget?.isConnected ? this.focusTarget : document.getElementById(this.focusTarget?.id)) || opener;
      if (!target) return;
      const parentModal = this.closest('quick-add-modal[open], modal-dialog[open]');
      if (parentModal && typeof trapFocus === 'function') trapFocus(parentModal, target);
      else target.focus({ preventScroll: true });
    }

    handleKeydown(event) {
      if (['Tab', 'Escape'].includes(event.key)) event.stopPropagation();
      const button = event.target.closest('.shoe-size-guide__row-button');
      if (!button || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      const rows = this.rows;
      const index = rows.indexOf(button.closest('[data-size-chart-row]'));
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
      this.selectRow(rows[next]);
      rows[next]?.querySelector('button').focus();
    }
  }

  customElements.define('product-size-chart', ProductSizeChart);
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-product-size-chart-open]');
    if (!opener) return;
    const guide = document.getElementById(opener.dataset.productSizeChartOpen)?.closest('product-size-chart');
    if (!guide) return;
    event.preventDefault();
    guide.show(opener);
  });
})();
